import React, { useCallback, useEffect, useState } from 'react';

import { Card, CardContent, CardHeader } from 'components/ui/card';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { useToast } from 'components/ui/use-toast';
import { usePaginatedData } from 'hooks/usePaginatedData';
import { adminContent, auth } from 'services/api';
import ContentAssignment from 'pages/Admin/ContentAssignment';
import FlowCompanionTools from 'pages/Admin/FlowCompanionTools';
import UserManagement from 'pages/Admin/UserManagement';
import { UserManagementList } from 'pages/Admin/UserManagement/UserManagementList';
import type {
  AdminUserListItem,
  UserListFilters,
  OrganizationOption,
} from 'pages/Admin/UserManagement/types';

type TabValue = 'content-assignment' | 'flow-companion' | 'user-management';

const UserTools: React.FC = () => {
  const { addToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabValue>('content-assignment');

  // ── Shared user selection state ──────────────────────────────────────

  const [selectedUser, setSelectedUser] = useState<AdminUserListItem | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const data = await auth.getOrganizations();
        setOrganizations(data?.organizations ?? []);
      } catch (err) {
        console.error('Failed to load organizations', err);
      }
    })();
  }, []);

  const fetchUsers = useCallback(
    async ({
      page: nextPage,
      pageSize: nextPageSize,
      search: nextSearch,
      filters: nextFilters,
    }: {
      page: number;
      pageSize: number;
      search?: string;
      filters: UserListFilters;
    }) => {
      const params: Record<string, string | number> = {
        page: nextPage,
        page_size: nextPageSize,
      };
      if (nextSearch) params.search = nextSearch;
      if (nextFilters.organization_id && nextFilters.organization_id !== 'all') {
        params.organization_id = nextFilters.organization_id;
      }

      const response = await adminContent.users.list(params);
      const data = response.data;

      if (data?.results) {
        return {
          data: data.results as AdminUserListItem[],
          total: typeof data.count === 'number' ? data.count : data.results.length,
        };
      }
      if (Array.isArray(data)) {
        return { data: data as AdminUserListItem[], total: data.length };
      }
      return { data: [], total: 0 };
    },
    [],
  );

  const {
    data: users,
    total,
    loading,
    error,
    page,
    pageSize,
    search,
    setSearch,
    filters,
    setFilters,
    setPage,
    refetch,
  } = usePaginatedData<AdminUserListItem, UserListFilters>({
    fetcher: fetchUsers,
    initialPageSize: 25,
    initialFilters: { organization_id: 'all' },
  });

  useEffect(() => {
    if (error) {
      addToast({ title: 'Error', description: error, variant: 'destructive' });
    }
  }, [addToast, error]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (total === 0 && page !== 1) {
      setPage(1);
    } else if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, pageSize, setPage, total]);

  const handleSelectUser = useCallback((user: AdminUserListItem) => {
    setSelectedUser(user);
    setIsCreatingNew(false);
  }, []);

  const handleOrganizationChange = useCallback(
    (value: string) => setFilters({ organization_id: value }),
    [setFilters],
  );

  const handlePageChange = useCallback(
    (nextPage: number) => setPage(nextPage),
    [setPage],
  );

  const handleStartCreate = useCallback(() => {
    setIsCreatingNew(true);
    setSelectedUser(null);
    setActiveTab('user-management');
  }, []);

  const handleUserCreated = useCallback(
    async (createdUser: AdminUserListItem) => {
      setIsCreatingNew(false);
      setSelectedUser(createdUser);
      await refetch({ force: true });
    },
    [refetch],
  );

  const handleUserUpdated = useCallback(
    async (updatedUser: AdminUserListItem) => {
      setSelectedUser(updatedUser);
      await refetch({ force: true });
    },
    [refetch],
  );

  const handleCancelCreate = useCallback(() => {
    setIsCreatingNew(false);
  }, []);

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <section className="space-y-6 px-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold text-foreground">Users</h1>
        <p className="text-sm text-muted-foreground">
          Manage manual per-user content assignments, Module Companion chat resets, and user records.
        </p>
      </div>

      <UserManagementList
        users={users}
        loading={loading}
        error={error}
        search={search}
        onSearchChange={setSearch}
        selectedUserId={selectedUser?.id ?? null}
        organizations={organizations}
        selectedOrganization={filters.organization_id ?? 'all'}
        onOrganizationChange={handleOrganizationChange}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={handlePageChange}
        onSelectUser={handleSelectUser}
        onCreateUser={handleStartCreate}
      />

      <Card disableAnimation maxWidth="none">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as TabValue)} className="w-full">
          <CardHeader>
            <TabsList className="bg-transparent justify-start px-2">
              <TabsTrigger value="content-assignment" className="px-2">
                Assignments
              </TabsTrigger>
              <TabsTrigger value="flow-companion" className="px-2">
                Module Companion
              </TabsTrigger>
              <TabsTrigger value="user-management" className="px-2">
                Profile &amp; Access
              </TabsTrigger>
            </TabsList>
          </CardHeader>
        </Tabs>

        <CardContent variant='form'>
          <div className={activeTab !== 'content-assignment' ? 'hidden' : undefined}>
            <ContentAssignment selectedUser={selectedUser} />
          </div>

          <div className={activeTab !== 'flow-companion' ? 'hidden' : undefined}>
            <FlowCompanionTools selectedUser={selectedUser} />
          </div>

          <div className={activeTab !== 'user-management' ? 'hidden' : undefined}>
            {activeTab === 'user-management' ? (
              <UserManagement
                selectedUser={selectedUser}
                isCreatingNew={isCreatingNew}
                organizations={organizations}
                onUserCreated={handleUserCreated}
                onUserUpdated={handleUserUpdated}
                onCancelCreate={handleCancelCreate}
              />
            ) : null}
          </div>
        </CardContent>
      </Card>
    </section>
  );
};

export default UserTools;
